export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'system' | ThemeMode;

const THEME_STORAGE_KEY = 'vuey-theme-mode';
const COLOR_MODE_STORAGE_KEY = 'vuey-color-mode';

function normalizeThemeMode(value: unknown): ThemeMode | null {
  return value === 'dark' || value === 'light' ? value : null;
}

function resolveSystemThemeMode(): ThemeMode {
  if (!import.meta.client) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function normalizeThemePreference(value: unknown): ThemePreference | null {
  return value === 'system' || value === 'dark' || value === 'light' ? value : null;
}

function resolveStoredThemePreference(): ThemePreference {
  if (!import.meta.client) return 'system';

  return normalizeThemePreference(window.localStorage.getItem(COLOR_MODE_STORAGE_KEY))
    || normalizeThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY))
    || 'system';
}

function resolveThemeMode(preference: ThemePreference): ThemeMode {
  if (preference !== 'system') return preference;
  return resolveSystemThemeMode();
}

function applyThemeModeToDocument(mode: ThemeMode) {
  if (!import.meta.client) return;

  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.style.colorScheme = mode;
}

export function useThemeMode() {
  const mode = useState<ThemeMode>('theme-mode', () => 'light');
  const preference = useState<ThemePreference>('theme-preference', () => 'system');
  const initialized = useState<boolean>('theme-mode-initialized', () => false);
  let systemModeQuery: MediaQueryList | null = null;
  let handleSystemModeChange: (() => void) | null = null;

  function setMode(nextMode: ThemeMode) {
    setPreference(nextMode);
  }

  function setPreference(nextPreference: ThemePreference) {
    preference.value = nextPreference;
    mode.value = resolveThemeMode(nextPreference);

    if (import.meta.client) {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, nextPreference);
      window.localStorage.setItem(THEME_STORAGE_KEY, mode.value);
    }

    applyThemeModeToDocument(mode.value);
  }

  function toggleMode() {
    setMode(mode.value === 'dark' ? 'light' : 'dark');
  }

  function initializeMode() {
    if (initialized.value) {
      applyThemeModeToDocument(mode.value);
      return;
    }

    const storedPreference = resolveStoredThemePreference();
    preference.value = storedPreference;
    mode.value = resolveThemeMode(storedPreference);
    initialized.value = true;
    applyThemeModeToDocument(mode.value);
  }

  if (import.meta.client) {
    onMounted(() => {
      initializeMode();

      systemModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      handleSystemModeChange = () => {
        if (preference.value !== 'system') return;
        mode.value = resolveSystemThemeMode();
        applyThemeModeToDocument(mode.value);
      };

      systemModeQuery.addEventListener('change', handleSystemModeChange);
    });

    onBeforeUnmount(() => {
      if (systemModeQuery && handleSystemModeChange) {
        systemModeQuery.removeEventListener('change', handleSystemModeChange);
      }
    });
  }

  return {
    mode: computed(() => mode.value),
    preference: computed(() => preference.value),
    isDark: computed(() => mode.value === 'dark'),
    setMode,
    setPreference,
    toggleMode,
    initializeMode,
  };
}
